import { useGetUserQuery, useUpdateUserMutation } from '@/services/innovorder/user/user.endpoints';
import { useRouteMatch } from 'react-router';
import { AccessFlags } from '@/services/innovorder/auth/auth.type';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUser } from '@/redux/user';
import { UserRoles } from '@/services/innovorder/user/user.type';
import { BRAND_EDITABLE_ACCESS_FLAGS } from '@/constants/brandEditableFlags';
import { ACCESS_FLAG_GROUPS, AccessFlagGroup, getAllGroupedFlags } from '@/constants/accessFlagsGroups';

// Flags only meaningful for restaurant-level users: they should be hidden
// when editing brand/admin users, who have the underlying capability by role.
const RESTAURANT_ONLY_FLAGS: string[] = [AccessFlags.CAN_CREATE_SKU];

interface FilteredGroup extends AccessFlagGroup {
    filteredFlags: string[];
    filteredSubsections?: { key: string; filteredFlags: string[] }[];
    totalCount: number;
}

interface useAccessFlagsControlVM {
    handleSave: () => void;
    displayedFlags: Record<string, boolean> | null;
    handleSwitch: (accessFlag: string) => void;
    isButtonDisabled: boolean;
    isButtonLoading: boolean;
    collapsedGroups: Record<string, boolean>;
    toggleGroup: (groupKey: string) => void;
    toggleAllGroups: () => void;
    areAllCollapsed: boolean;
    filteredGroups: FilteredGroup[];
}

const useAccessFlagsControlVM = (): useAccessFlagsControlVM => {
    const { params } = useRouteMatch<{ userId: string }>();
    const { data } = useGetUserQuery({ userId: Number(params.userId) });
    const currentUser = useSelector(getUser);
    const [displayedFlags, setDisplayedFlags] = useState<Record<string, boolean> | null>(null);
    const [changes, setChanges] = useState<string[]>([]);
    const [updateUser, { isLoading }] = useUpdateUserMutation();
    const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

    // Determine which flags the current user can edit
    const editableFlags = useMemo(() => {
        if (currentUser?.role === UserRoles.admin) {
            return Object.values(AccessFlags);
        }
        if (currentUser?.role === UserRoles.brand && currentUser?.accessFlags?.[AccessFlags.CAN_MANAGE_BO_USERS]) {
            return BRAND_EDITABLE_ACCESS_FLAGS;
        }
        return [];
    }, [currentUser?.role, currentUser?.accessFlags]);

    const handleSave = useCallback(() => {
        data && displayedFlags && updateUser({ ...data, accessFlags: displayedFlags });
        setChanges([]);
    }, [data, displayedFlags]);

    const handleSwitch = useCallback(
        (accessFlag: string) => {
            if (displayedFlags) {
                if (accessFlag === 'MENU_PRODUCT_EDIT') {
                    setDisplayedFlags((prev) => ({
                        ...prev,
                        MENU_PRODUCT_EDIT_PRICE_ONLY: false,
                        MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY: false,
                    }));
                }
                if (accessFlag === 'MENU_PRODUCT_EDIT_PRICE_ONLY') {
                    setDisplayedFlags((prev) => ({
                        ...prev,
                        MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY: false,
                    }));
                }
                if (accessFlag === 'MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY') {
                    setDisplayedFlags((prev) => ({
                        ...prev,
                        MENU_PRODUCT_EDIT_PRICE_ONLY: false,
                    }));
                }
                setDisplayedFlags((prev) => ({
                    ...prev,
                    [accessFlag]: !displayedFlags[accessFlag],
                }));
            }

            setChanges((currentChangesList) => {
                const flagDoesNotExist = currentChangesList.indexOf(accessFlag) === -1;
                return flagDoesNotExist
                    ? [...currentChangesList, accessFlag]
                    : currentChangesList.filter((flag) => flag != accessFlag);
            });
        },
        [displayedFlags],
    );
    const isButtonDisabled = useMemo(() => !changes.length, [changes]);

    const toggleGroup = useCallback((groupKey: string) => {
        setCollapsedGroups((prev) => ({
            ...prev,
            [groupKey]: !prev[groupKey],
        }));
    }, []);

    // Filter groups to only show flags that the current user can edit
    const filteredGroups = useMemo((): FilteredGroup[] => {
        if (!displayedFlags) return [];

        const allGroupedFlags = getAllGroupedFlags();
        const isRestaurantTarget = data?.role === UserRoles.restaurant;

        // Restaurant-only flags are visible only when the target user is restaurant;
        // for brand/admin users they are kept in displayedFlags (so the save payload
        // preserves them) but never rendered.
        const isFlagVisible = (flag: string): boolean => {
            if (!(flag in displayedFlags)) return false;
            if (RESTAURANT_ONLY_FLAGS.includes(flag) && !isRestaurantTarget) return false;
            return true;
        };

        const groups = ACCESS_FLAG_GROUPS.map((group) => {
            const filteredFlags = group.flags.filter(isFlagVisible);
            const filteredSubsections = group.subsections
                ?.map((sub) => ({
                    key: sub.key,
                    filteredFlags: sub.flags.filter(isFlagVisible),
                }))
                .filter((sub) => sub.filteredFlags.length > 0);

            const totalCount =
                filteredFlags.length +
                (filteredSubsections?.reduce((sum, sub) => sum + sub.filteredFlags.length, 0) ?? 0);

            return {
                ...group,
                filteredFlags,
                filteredSubsections,
                totalCount,
            };
        }).filter((group) => group.totalCount > 0);

        // Find flags that are not in any group (fallback to "others" section)
        const ungroupedFlags = Object.keys(displayedFlags).filter(
            (flag) => !allGroupedFlags.includes(flag) && isFlagVisible(flag),
        );

        if (ungroupedFlags.length > 0) {
            groups.push({
                key: 'others',
                icon: '📋',
                flags: ungroupedFlags,
                filteredFlags: ungroupedFlags,
                filteredSubsections: undefined,
                totalCount: ungroupedFlags.length,
            });
        }

        return groups;
    }, [displayedFlags, data?.role]);

    const areAllCollapsed = useMemo(() => {
        const groupKeys = filteredGroups.map((g) => g.key);
        return groupKeys.length > 0 && groupKeys.every((key) => collapsedGroups[key]);
    }, [collapsedGroups, filteredGroups]);

    const toggleAllGroups = useCallback(() => {
        const groupKeys = filteredGroups.map((g) => g.key);
        const newState = !areAllCollapsed;
        setCollapsedGroups(groupKeys.reduce((acc, key) => ({ ...acc, [key]: newState }), {}));
    }, [areAllCollapsed, filteredGroups]);

    useEffect(() => {
        if (!data) return;
        // Only show flags that the current user can edit
        const receivedAccessFlagsState = editableFlags.reduce((acc, currentFlag) => {
            let flagExists = Object.keys(data.accessFlags).includes(currentFlag);
            if (
                currentFlag === 'MENU_PRODUCT_EDIT_PRICE_ONLY' ||
                currentFlag === 'MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY'
            ) {
                flagExists = flagExists && Object.keys(data.accessFlags).includes('MENU_PRODUCT_EDIT');
            }
            return { ...acc, [currentFlag]: flagExists };
        }, {} as Record<string, boolean>);

        setDisplayedFlags(receivedAccessFlagsState);
    }, [data, editableFlags]);

    return {
        handleSave,
        displayedFlags,
        handleSwitch,
        isButtonDisabled,
        isButtonLoading: isLoading,
        collapsedGroups,
        toggleGroup,
        toggleAllGroups,
        areAllCollapsed,
        filteredGroups,
    };
};

export default useAccessFlagsControlVM;
